import React from "react";
import { graphql } from "react-relay";

import * as Sentry from "@sentry/browser";
import { EventEmitter } from "events";

import { DebugLogger } from "~/lib/log";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";

const log = new DebugLogger("[OuterObjectContext]").log;

export const OUTER_OBJECT_CONTEXT_CHANGE_EVENT = "outerObjectContextId.change" as const;

const MarkRelatedNotificationsAsRead = graphql`
	mutation outerObjectContext_MarkRelatedNotificationsAsReadMutation($outerObjectIds: [ID!]!) {
		NotificationCenter_MarkRelatedAsRead(outerObjectIds: $outerObjectIds) {
			# To update notification center
			changedNotifications {
				id
				unread
			}

			notificationCenter {
				id
				unreadNotificationCount
			}
		}
	}
`;

async function markRelatedNotificationsAsRead(outerObjectIds: readonly string[]) {
	try {
		await commitMutationPromise(environment, {
			mutation: MarkRelatedNotificationsAsRead,
			variables: { outerObjectIds },
		});
	} catch (e) {
		Sentry.captureException(e);
	}
}

/**
 * Holds the outer object ID.
 * At the moment a stream ID for the (meeting and streams) pages and a feedback ID for feedback pages respectively.
 * Use cases include dismissing notifications when the user navigates to the page for that notification.
 * As well as closing the inMeeting button if the user manually navigates to the page.
 *
 * An important note is that in most of the places this is called it will only update **after** the outer graphql
 * request / response is complete.
 */
export class OuterObjectContextClass {
	private _outerObjectContextIds = new Set<string>();
	private emitter: EventEmitter;

	constructor() {
		this.emitter = new EventEmitter();
	}

	// Disallow others from mutating this directly
	get outerObjectContextIds(): ReadonlySet<string> {
		return this._outerObjectContextIds;
	}

	/**
	 * Listens for changes to the outer object.
	 * @returns a function that can be called to cancel the listener.
	 */
	on(event: typeof OUTER_OBJECT_CONTEXT_CHANGE_EVENT, listener: (arg: string | null) => void) {
		this.emitter.on(event, listener);

		return () => void this.emitter.off(event, listener);
	}

	off(event: typeof OUTER_OBJECT_CONTEXT_CHANGE_EVENT, listener: (arg: string | null) => void) {
		this.emitter.off(event, listener);
	}

	private emit(id: string | null) {
		this.emitter.emit(OUTER_OBJECT_CONTEXT_CHANGE_EVENT, id);
	}

	/**
	 * Sets the outer ID.
	 * At time of writing this Clears any notifications relating to an object, and prevent any incoming
	 * notifications about that object from showing until the clear ID is unset.
	 * As well as dismissing any inMeeting popups relating to the object.
	 * ({@see unsetClearId}).
	 *
	 * @param id A Relay global ID
	 */
	setClearId(id: string): void {
		log("Set clear ID", { id });
		this.emit(id);
		this._outerObjectContextIds.add(id);
		this.markRelatedNotificationsAsRead();
	}

	unsetClearId(id: string): void {
		log("Unset clear ID");
		this.emit(null);
		this._outerObjectContextIds.delete(id);
	}

	isCleared(id: string): boolean {
		return this.outerObjectContextIds.has(id);
	}

	async markRelatedNotificationsAsRead() {
		await markRelatedNotificationsAsRead([...this._outerObjectContextIds]);
	}
}

export const OuterObjectContext = React.createContext(new OuterObjectContextClass());
OuterObjectContext.displayName = "OuterObjectContext";

/**
 * Clear notifications for a given collapse key for the lifetime of the
 * rendering component.
 */
export function useSetOuterObjectContext(objectId: string) {
	const ctx = React.useContext(OuterObjectContext);

	React.useEffect(() => {
		ctx?.setClearId(objectId);
		return () => {
			ctx?.unsetClearId(objectId);
		};
	}, [objectId, ctx]);
}

/**
 * A component that sets the outer object context while it's mounted.
 *
 * This is useful for setting the outer object context in class components
 * where this.context is already in use.
 */
export function SetOuterObjectContext({ objectId }: { objectId: string }) {
	useSetOuterObjectContext(objectId);
	return null;
}
